import * as THREE from 'three';
import { ViewOrientationMaterialFactory } from '../../material/ViewOrientationMaterialFactory';
import { getStandardViewDefinition } from './standardViewDefinitions';
import { StandardViewType, ViewOrientationTargetKind } from './standardViewTypes';
import { ViewOrientationElementMesh } from './ViewOrientationElementMesh';

/**
 * 主面网格。
 *
 * 设计说明：
 * 1. 该类一次只生成一个 8 边主面，禁止在内部批量创建 6 个面；
 * 2. 主面的具体装配位置由外部容器决定，当前类只负责单面几何与自身姿态；
 * 3. 通过继承 ViewOrientationElementMesh，继续保留命中语义和交互链兼容性。
 */
export class ViewOrientationFaceMesh extends ViewOrientationElementMesh {
  /**
   * 构造单个主面 mesh。
   *
   * @param viewType 标准视图类型。
   * @throws 当视图定义不是主面类型时抛出错误。
   */
  public constructor(viewType: StandardViewType) {
    const definition = getStandardViewDefinition(viewType);
    if (definition.kind !== ViewOrientationTargetKind.Face) {
      throw new Error(`主面类型错误：${viewType}`);
    }
    const metrics = ViewOrientationFaceMesh.createMetrics();

    const material = ViewOrientationMaterialFactory.createBasicMaterial(definition.kind);

    super({
      viewType,
      kind: definition.kind,
      label: definition.label,
      geometry: ViewOrientationFaceMesh.createFaceGeometry(metrics),
      material,
      renderOrder: ViewOrientationElementMesh.RENDER_ORDER,
    });

    this.name = `view-orientation-face-${viewType}`;
    this.applyFacePlacement(viewType, metrics);
  }

  /**
   * 释放资源。
   */
  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }

  /**
   * 创建主面尺寸参数。
   *
   * @returns 尺寸参数。
   */
  private static createMetrics(): {
    outerHalfSize: number;
    faceLength: number;
    edgeCut: number;
    straightEdgeLength: number;
    cornerEdgeLength: number;
  } {
    const size = ViewOrientationElementMesh.SIZE;
    const outerHalfSize = size / 2;
    const edgeCut = size / (4 + 4 * Math.SQRT2);
    const faceLength = size - edgeCut * 2;
    const straightEdgeLength = faceLength - edgeCut * 2;
    const cornerEdgeLength = Math.SQRT2 * edgeCut;

    return {
      outerHalfSize,
      faceLength,
      edgeCut,
      straightEdgeLength,
      cornerEdgeLength,
    };
  }

  /**
   * 创建单个 8 边主面几何。
   *
   * @param faceLength 主面外接边长。
   * @param edgeCut 倒角切边长度。
   * @returns ShapeGeometry。
   */
  private static createOctagonGeometry(
    faceLength: number,
    edgeCut: number,
  ): THREE.ShapeGeometry {
    const halfSize = faceLength / 2;
    const shape = new THREE.Shape()
      .moveTo(-halfSize + edgeCut, -halfSize)
      .lineTo(halfSize - edgeCut, -halfSize)
      .lineTo(halfSize, -halfSize + edgeCut)
      .lineTo(halfSize, halfSize - edgeCut)
      .lineTo(halfSize - edgeCut, halfSize)
      .lineTo(-halfSize + edgeCut, halfSize)
      .lineTo(-halfSize, halfSize - edgeCut)
      .lineTo(-halfSize, -halfSize + edgeCut)
      .lineTo(-halfSize + edgeCut, -halfSize);

    return new THREE.ShapeGeometry(shape);
  }

  /**
   * 创建主面几何。
   *
   * @param metrics 尺寸参数。
   * @returns ShapeGeometry。
   */
  private static createFaceGeometry(metrics: {
    outerHalfSize: number;
    faceLength: number;
    edgeCut: number;
    straightEdgeLength: number;
    cornerEdgeLength: number;
  }): THREE.ShapeGeometry {
    void metrics.outerHalfSize;
    void metrics.straightEdgeLength;
    void metrics.cornerEdgeLength;
    return ViewOrientationFaceMesh.createOctagonGeometry(metrics.faceLength, metrics.edgeCut);
  }

  /**
   * 应用主面装配位置与朝向。
   *
   * @param viewType 标准视图类型。
   * @param metrics 尺寸参数。
   */
  private applyFacePlacement(
    viewType: StandardViewType,
    metrics: {
      outerHalfSize: number;
      faceLength: number;
      edgeCut: number;
      straightEdgeLength: number;
      cornerEdgeLength: number;
    },
  ): void {
    void metrics.faceLength;
    void metrics.edgeCut;
    void metrics.straightEdgeLength;
    void metrics.cornerEdgeLength;
    const position = ViewOrientationFaceMesh.resolveFacePosition(viewType);
    this.position.set(
      position[0] * metrics.outerHalfSize,
      position[1] * metrics.outerHalfSize,
      position[2] * metrics.outerHalfSize,
    );
    this.lookAt(this.position.clone().multiplyScalar(1.7));
  }

  /**
   * 解析主面对应的归一化位置。
   *
   * @param viewType 标准视图类型。
   * @returns 归一化位置。
   */
  private static resolveFacePosition(
    viewType: StandardViewType,
  ): readonly [number, number, number] {
    switch (viewType) {
      case StandardViewType.Right:
        return [1, 0, 0];
      case StandardViewType.Top:
        return [0, 1, 0];
      case StandardViewType.Front:
        return [0, 0, 1];
      case StandardViewType.Left:
        return [-1, 0, 0];
      case StandardViewType.Bottom:
        return [0, -1, 0];
      case StandardViewType.Back:
        return [0, 0, -1];
      default:
        throw new Error(`不支持的主面视图类型：${viewType}`);
    }
  }
}
